/**
 * DateOverrideForm
 * Add a date override (vacation / custom hours) for a Gutachter.
 */
import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { format, addDays } from "date-fns";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button, Input, Label } from "@jourfx/ui";

interface DateOverrideFormProps {
  gutachterId: string;
  onCreated?: () => void;
}

interface TimeWindow {
  start: string;
  end: string;
}

export function DateOverrideForm({
  gutachterId,
  onCreated,
}: DateOverrideFormProps) {
  const createOverride = useMutation(api.availability.index.createOverride);

  const [date, setDate] = useState(
    format(addDays(new Date(), 1), "yyyy-MM-dd"),
  );
  const [type, setType] = useState<"unavailable" | "custom">("unavailable");
  const [reason, setReason] = useState("");
  const [slots, setSlots] = useState<TimeWindow[]>([
    { start: "08:00", end: "12:00" },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await createOverride({
        gutachterId: gutachterId as never,
        date,
        type,
        reason: reason || undefined,
        slots: type === "custom" ? JSON.stringify(slots) : undefined,
      });
      // Reset
      setDate(format(addDays(new Date(), 1), "yyyy-MM-dd"));
      setType("unavailable");
      setReason("");
      setSlots([{ start: "08:00", end: "12:00" }]);
      onCreated?.();
    } catch (err) {
      console.error("Failed to create override:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="override-form">
      <div className="space-y-2">
        <Label>Datum</Label>
        <Input
          type="date"
          value={date}
          min={format(new Date(), "yyyy-MM-dd")}
          onChange={(e) => setDate(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label>Art</Label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setType("unavailable")}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              type === "unavailable"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input hover:bg-accent"
            }`}
          >
            Abwesend (ganzer Tag)
          </button>
          <button
            type="button"
            onClick={() => setType("custom")}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              type === "custom"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input hover:bg-accent"
            }`}
          >
            Benutzerdefiniert
          </button>
        </div>
      </div>

      {type === "custom" && (
        <div className="space-y-2">
          <Label>Zeitfenster</Label>
          {slots.map((slot, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="time"
                value={slot.start}
                onChange={(e) => {
                  const next = [...slots];
                  next[idx] = { ...next[idx], start: e.target.value };
                  setSlots(next);
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <span className="text-sm text-muted-foreground">bis</span>
              <input
                type="time"
                value={slot.end}
                onChange={(e) => {
                  const next = [...slots];
                  next[idx] = { ...next[idx], end: e.target.value };
                  setSlots(next);
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {slots.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  onClick={() => setSlots(slots.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1 text-xs"
            onClick={() =>
              setSlots([...slots, { start: "08:00", end: "12:00" }])
            }
          >
            <Plus className="h-3 w-3" />
            Zeitfenster hinzufügen
          </Button>
        </div>
      )}

      <div className="space-y-2">
        <Label>Grund (optional)</Label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="z.B. Urlaub, Fortbildung..."
        />
      </div>

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Plus className="mr-2 h-4 w-4" />
        )}
        Abwesenheit hinzufügen
      </Button>
    </form>
  );
}
